// @flow
import type { TridentCampaignDetails } from 'types/trident-campaigns';
import React, { Component } from 'react';
import type { Country, City } from 'types/country';
import { get, isEmpty, isEqual, remove } from 'lodash';
import { getScenarioConstraints, getTimeConstraints, getDelayConstraints, getExperimentConstraints } from 'components/form/trident-campaigns/constraints';
import { Dropdown, Grid, Popup, Icon } from 'semantic-grab-ui/src';
import { getStartingNodeOptions, getModal } from 'omega-rtc/utils/trident-node-utils';
import TridentTreatmentBuilder from './TridentTreatmentBuilder';
import 'components/form/trident-campaigns/WidgetComponent.scss';
import 'views/trident-campaigns/TridentCampaigns.scss';
import { RandomizerForm, TimeTriggerForm, ActionForm, ConditionForm, ScenarioForm, CounterForm, CountConditionForm, DelayForm, ExperimentForm, EnumerationForm } from 'components/form/trident-campaigns';
import { nodeTypes } from 'data/trident-types';

type Props = {
  model: TridentCampaignDetails,
  canActivate: boolean,
  onModelChange: (_?: Object) => void,
  cities: Array<City>,
  country: Country,
  handleNodePreview: Function,
  clickedNode: string,
  handleDeleteNode: Function,
  addDefaultExperiment: Function,
  userPermissions: Object,
  listRuleTypes: Object,
};

export default class TridentCampaignBuilder extends Component {
  props: Props;
  constructor(props: Props) {
    super(props);
    // code to open modals accordingly
    this.modalContentMap = {
      [nodeTypes.actions]: this.getActionForm,
      [nodeTypes.scenarios]: this.getScenarioForm,
      [nodeTypes.count]: this.getCounterForm,
      [nodeTypes.time]: this.getTimeTriggerForm,
      [nodeTypes.schedule]: this.getScheduleForm,
      [nodeTypes.enumeration]: this.getEnumerationForm,
      [nodeTypes.condition]: this.getConditionForm,
      [nodeTypes.countCondition]: this.getCountConditionForm,
      [nodeTypes.delay]: this.getDelayForm,
      [nodeTypes.experiment]: this.getExperimentForm,
      [nodeTypes.randomizer]: this.getRandomizerForm,
    };
  }

  renderModal = (title, formComponent, constraints) => {
    const { onModelChange, model, country, cities, canActivate, handleDeleteNode, listRuleTypes, addDefaultExperiment } = this.props;
    const { scenarioIndex, currentNode, parentNode, parentKeyList, addNode } = this.props;
    const formProps = {
      onModelChange,
      model,
      scenarioIndex,
      country,
      cities,
      canActivate,
      currentNode,
      parentNode,
      parentKeyList,
      handleDeleteNode,
      handleAddNode: addNode,
      listRuleTypes,
      addDefaultExperiment,
      handleCloseModal: this.props.closeModal,
      isAdd: true,
      constraints,
    };
    return getModal(title, formComponent, formProps);
  }
  getScenarioForm = () => {
    const scenarioConstraints = getScenarioConstraints(this.props.currentNode, this.props.scenarioIndex);
    return this.renderModal('Add Scenario', ScenarioForm, scenarioConstraints);
  }

  getTimeTriggerForm = () => {
    const timeConstraints = getTimeConstraints(this.props.currentNode);
    return this.renderModal('Add Time Trigger', TimeTriggerForm, timeConstraints);
  }

  getScheduleForm = () => {
    const timeConstraints = getTimeConstraints(this.props.currentNode);
    return this.renderModal('Add Schedule', TimeTriggerForm, timeConstraints);
  }

  getConditionForm = () => {
    return this.renderModal('Add Condition', ConditionForm, {});
  }

  getDelayForm = () => {
    const delayConstraints = getDelayConstraints(this.props.currentNode);
    return this.renderModal('Add Delay', DelayForm, delayConstraints);
  }

  getExperimentForm = () => {
    const experimentConstraints = getExperimentConstraints(this.props.currentNode);
    return this.renderModal('Add Experiment', ExperimentForm, experimentConstraints);
  }

  getRandomizerForm = () => {
    return this.renderModal('Add Action Randomizer', RandomizerForm, {});
  }

  getCounterForm = () => {
    return this.renderModal('Add Counter', CounterForm, {});
  }

  getCountConditionForm = () => {
    return this.renderModal('Add Count Condition', CountConditionForm, {});
  }

  getActionForm = () => {
    return this.renderModal('Add Action', ActionForm, {});
  }

  getEnumerationForm = () => {
    return this.renderModal('Add Enumeration', EnumerationForm, {});
  }

  shouldComponentUpdate = (nextProps, nextState) => {
    return !isEqual(nextProps, this.props) || !isEqual(nextState, this.state);
  }
  modal = () => {
    const { nodeType, model, currentNode } = this.props;
    const shouldShowModal = !this.props.renderNodePreview && this.props.showModal;
    const actualCurrentType = get(model.nodes, [currentNode, 'type']);
    const storeAndStateSynced = actualCurrentType === nodeType;
    if (shouldShowModal && storeAndStateSynced) { return this.modalContentMap[this.props.nodeType](); }
  }

  getDivStyleChainScenario = (numScenarios: number) => {
    const minHeight = ((numScenarios - 2) * 110) + 350;
    return {
      marginTop: 80,
      minHeight,
    };
  };

  getStartingNodeStyle = (node: Object) => {
    const treatmentDivStyle = {
      minHeight: 200,
      marginTop: 80,
    };

    const subScenarios = get(node, 'data.scenarios', []);
    return subScenarios.length > 1 ? this.getDivStyleChainScenario(subScenarios.length) : treatmentDivStyle;
  };

  render() {
    const { model, canActivate, appConfig } = this.props;

    const addBtnStyle = {
      fontSize: '2em',
      color: '#3498DB',
      padding: '25px 17px 15px 17px',
      border: '1px #ccc solid',
      borderRadius: 5,
    };
    const bottomSpaceHolder = {
      minHeight: 50,
    };

    const targetUserType = get(model, 'targetUserType', '');
    const owner = get(model, 'owner', '');
    const startingNodeOptions = getStartingNodeOptions(this.props.listRuleTypes, targetUserType);
    if (get(appConfig, 'tridentScheduleNodeEnabled', true)) {
      startingNodeOptions.push(nodeTypes.schedule);
    }
    if (get(appConfig, 'tridentTimeNodeDisabled', false)) {
      remove(startingNodeOptions, o => o === nodeTypes.time);
    }

    return (
      <div className="horizontalScroll">
        {this.props.treatmentStartKeysList.map((currentNodeKey, index) => {
          return (
            <div
              key={currentNodeKey}
              style={this.getStartingNodeStyle(model.nodes[currentNodeKey])}
            >
              <TridentTreatmentBuilder
                key={index}
                onModelChange={this.props.onModelChange}
                model={model}
                currentNodeKey={currentNodeKey}
                handleNodePreview={this.props.handleNodePreview}
                handleAddNode={this.props.addNode}
                canActivate={canActivate}
                clickedNode={this.props.clickedNode}
                appConfig={appConfig}
              />
            </div>
          );
        })}
        <br />
        {!isEmpty(targetUserType) && !isEmpty(owner) ? (
          <React.Fragment>
            <Grid>
              <Grid.Column className="trident-campaign-builder" textAlign="left" verticalAlign="middle">
                <Dropdown icon="plus" style={addBtnStyle} floating basic className="icon">
                  <Dropdown.Menu>
                    {startingNodeOptions.map(node => {
                      return (
                        <Dropdown.Item key={node} onClick={() => this.props.addNode(node)}>
                          {`Add ${node}`}
                          {<Popup
                            trigger={<Icon name="info circle" />}
                            content={`Add ${node}`}
                            size="mini"
                          />}
                        </Dropdown.Item>
                      );
                    })}
                  </Dropdown.Menu>
                </Dropdown>
              </Grid.Column>
            </Grid>
            <p style={bottomSpaceHolder} />
          </React.Fragment>
        ) : (
          <p>Please select target user type and owner</p>
        )
        }
        <br />
        <br />
        <br />
        {this.modal()}
      </div>
    );
  }
}
